import React, { useState, useEffect } from 'react';
import {
  Row, Col, Form, FormGroup, Label, Input, Button, Table, Card, CardBody,
  Modal, ModalHeader, ModalBody, ModalFooter, Pagination, PaginationItem, PaginationLink,
  Spinner, Alert, Badge
} from 'reactstrap';
import axios from 'axios';
import {
  FaTrash, FaEdit, FaUpload, FaPlay, FaHeart, FaRegHeart, FaFire, FaImage,
  FaMusic, FaBuilding, FaPhone, FaVideo, FaCheckCircle, FaClock,
  FaTag, FaHashtag, FaEye, FaCalendarAlt, FaUser
} from 'react-icons/fa';

const ManageReels = () => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [hotTop, setHotTop] = useState(false);
  const [reels, setReels] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Business Information states
  const [businessName, setBusinessName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Reel Categories state
  const [reelCategories, setReelCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editVideoPreview, setEditVideoPreview] = useState(null);
  const [editThumbnailFile, setEditThumbnailFile] = useState(null);
  const [editThumbnailPreview, setEditThumbnailPreview] = useState(null);
  const [editAudioFile, setEditAudioFile] = useState(null);
  const [editAudioPreview, setEditAudioPreview] = useState(null);
  const [editAudioDuration, setEditAudioDuration] = useState(0);
  const [editId, setEditId] = useState(null);
  const [editLikeCount, setEditLikeCount] = useState(0);
  const [editIsLiked, setEditIsLiked] = useState(false);
  const [editHotTop, setEditHotTop] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState('');
  const [editBusinessName, setEditBusinessName] = useState('');
  const [editPhoneNumber, setEditPhoneNumber] = useState('');

  // Video Modal State
  const [videoModal, setVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const reelsPerPage = 5;
  const totalPages = Math.ceil(reels.length / reelsPerPage);

  const paginatedReels = reels.slice(
    (currentPage - 1) * reelsPerPage,
    currentPage * reelsPerPage
  );

  const API_BASE_URL = 'https://api.editezy.com/api/admin';

  // Fetch reel categories
  const fetchReelCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/reelcategories`);
      if (response.data.success) {
        setReelCategories(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching reel categories:', err);
      setError('Failed to load reel categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchReels = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/getallreels`);
      setReels(res.data.reels || []);
    } catch (err) {
      console.error('Error fetching reels:', err);
      setError('Failed to fetch reels. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReels();
    fetchReelCategories();
  }, []);

  // Validate audio duration (max 30 seconds)
  const validateAudioDuration = (file, callback) => {
    if (!file) return callback(true);
    const audio = new Audio();
    const objectUrl = URL.createObjectURL(file);
    audio.src = objectUrl;
    audio.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(objectUrl);
      const duration = audio.duration;
      if (duration > 30) {
        callback(false, duration);
      } else {
        callback(true, duration);
      }
    });
    audio.addEventListener('error', () => {
      URL.revokeObjectURL(objectUrl);
      callback(false, 0);
    });
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('video/')) {
      setError('Please select a video file only');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('Maximum file size is 50MB');
      return;
    }

    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('image/')) {
      setError('Please select an image file for thumbnail');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Maximum thumbnail size is 5MB');
      return;
    }

    setThumbnailFile(file);
    setThumbnailPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('audio/')) {
      setError('Please select an audio file (MP3, WAV, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Maximum audio file size is 10MB');
      return;
    }

    validateAudioDuration(file, (isValid, duration) => {
      if (!isValid) {
        setError(`Audio duration is ${duration.toFixed(1)} seconds. Maximum allowed is 30 seconds.`);
        setAudioFile(null);
        setAudioPreview(null);
        setAudioDuration(0);
      } else {
        setAudioFile(file);
        setAudioPreview(URL.createObjectURL(file));
        setAudioDuration(duration);
        setError(null);
      }
    });
  };

  const handleEditVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('video/')) {
      setError('Please select a video file only');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('Maximum file size is 50MB');
      return;
    }

    setEditVideoFile(file);
    setEditVideoPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleEditThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('image/')) {
      setError('Please select an image file for thumbnail');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Maximum thumbnail size is 5MB');
      return;
    }

    setEditThumbnailFile(file);
    setEditThumbnailPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleEditAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('audio/')) {
      setError('Please select an audio file (MP3, WAV, etc.)');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Maximum audio file size is 10MB');
      return;
    }

    validateAudioDuration(file, (isValid, duration) => {
      if (!isValid) {
        setError(`Audio duration is ${duration.toFixed(1)} seconds. Maximum allowed is 30 seconds.`);
        setEditAudioFile(null);
        setEditAudioPreview(null);
        setEditAudioDuration(0);
      } else {
        setEditAudioFile(file);
        setEditAudioPreview(URL.createObjectURL(file));
        setEditAudioDuration(duration);
        setError(null);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!videoFile) {
      setError('Please upload a video');
      return;
    }
    if (!thumbnailFile) {
      setError('Please upload a thumbnail image (Mandatory)');
      return;
    }
    if (!selectedCategory) {
      setError('Please select a reel category');
      return;
    }
    if (!businessName.trim()) {
      setError('Please enter business name');
      return;
    }
    if (!phoneNumber.trim()) {
      setError('Please enter phone number');
      return;
    }
    if (audioFile && audioDuration > 30) {
      setError(`Audio duration exceeds 30 seconds (${audioDuration.toFixed(1)}s)`);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('thumbnail', thumbnailFile);
    if (audioFile) formData.append('audio', audioFile);
    formData.append('hotTop', hotTop);
    formData.append('reelCategoryId', selectedCategory);
    formData.append('businessName', businessName.trim());
    formData.append('phoneNumber', phoneNumber.trim());

    try {
      await axios.post(`${API_BASE_URL}/createreel`, formData);
      setSuccess('Reel created successfully!');
      fetchReels();
      setVideoFile(null);
      setVideoPreview(null);
      setThumbnailFile(null);
      setThumbnailPreview(null);
      setAudioFile(null);
      setAudioPreview(null);
      setAudioDuration(0);
      setHotTop(false);
      setSelectedCategory('');
      setBusinessName('');
      setPhoneNumber('');

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error creating reel:', err);
      setError(err.response?.data?.message || 'Failed to create reel');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reel?')) return;

    setError(null);
    setSuccess(null);

    try {
      await axios.delete(`${API_BASE_URL}/deletereel/${id}`);
      setSuccess('Reel deleted successfully!');
      fetchReels();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting reel:', err);
      setError(err.response?.data?.message || 'Failed to delete reel');
    }
  };

  const handleLikeToggle = async (reel) => {
    const formData = new FormData();
    const newLike = !reel.isLiked;
    const count = newLike ? reel.likeCount + 1 : Math.max(0, reel.likeCount - 1);

    formData.append('isLiked', newLike);
    formData.append('likeCount', count);
    formData.append('hotTop', reel.hotTop);
    if (reel.reelCategoryId) {
      formData.append('reelCategoryId', reel.reelCategoryId);
    }
    if (reel.businessName) {
      formData.append('businessName', reel.businessName);
    }
    if (reel.phoneNumber) {
      formData.append('phoneNumber', reel.phoneNumber);
    }

    try {
      await axios.put(`${API_BASE_URL}/updatereel/${reel._id}`, formData);
      fetchReels();
    } catch (err) {
      console.error('Error toggling like:', err);
      setError('Failed to update like status');
    }
  };

  const openEditModal = (reel) => {
    setEditId(reel._id);
    setEditLikeCount(reel.likeCount);
    setEditIsLiked(reel.isLiked);
    setEditHotTop(reel.hotTop || false);
    setEditCategoryId(reel.reelCategoryId || '');
    setEditBusinessName(reel.businessName || '');
    setEditPhoneNumber(reel.phoneNumber || '');
    setEditVideoFile(null);
    setEditVideoPreview(null);
    setEditThumbnailFile(null);
    setEditThumbnailPreview(null);
    setEditAudioFile(null);
    setEditAudioPreview(null);
    setEditAudioDuration(0);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const formData = new FormData();

    if (editVideoFile) formData.append('video', editVideoFile);
    if (editThumbnailFile) formData.append('thumbnail', editThumbnailFile);
    if (editAudioFile) formData.append('audio', editAudioFile);

    formData.append('likeCount', editLikeCount);
    formData.append('isLiked', editIsLiked);
    formData.append('hotTop', editHotTop);
    formData.append('reelCategoryId', editCategoryId);
    formData.append('businessName', editBusinessName.trim());
    formData.append('phoneNumber', editPhoneNumber.trim());

    try {
      await axios.put(`${API_BASE_URL}/updatereel/${editId}`, formData);
      setSuccess('Reel updated successfully!');
      setEditModalOpen(false);
      fetchReels();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating reel:', err);
      setError(err.response?.data?.message || 'Failed to update reel');
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPagination = () => {
    const pages = [];
    const delta = 1;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  };

  const openVideoModal = (url) => {
    setSelectedVideo(url);
    setVideoModal(true);
  };

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    if (!categoryId) return 'N/A';
    const category = reelCategories.find(cat => cat._id === categoryId);
    return category ? category.name : 'N/A';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="container-fluid px-4 py-4">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="text-primary fw-bold mb-1">
            <FaVideo className="me-2" /> Manage Reels
          </h4>
          <p className="text-muted mb-0">Create and manage your video reels</p>
        </div>
        <Badge color="primary" className="p-2 px-3">
          <FaHashtag className="me-1" /> Total: {reels.length} Reels
        </Badge>
      </div>

      {error && (
        <Alert color="danger" className="mb-4 border-0 shadow-sm" toggle={() => setError(null)}>
          <FaFire className="me-2" /> {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" className="mb-4 border-0 shadow-sm" toggle={() => setSuccess(null)}>
          <FaCheckCircle className="me-2" /> {success}
        </Alert>
      )}

      <Row className="g-4">
        {/* Upload Section */}
        <Col xs={12} lg={5}>
          <Card className="shadow-sm border-0 h-100">
            <CardBody className="p-4">
              <div className="d-flex align-items-center mb-4">
                <div className="bg-primary bg-opacity-10 p-2 rounded me-2">
                  <FaUpload className="text-primary" size={20} />
                </div>
                <h5 className="text-primary fw-bold mb-0">Create New Reel</h5>
              </div>

              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label className="fw-bold text-secondary">
                    <FaTag className="me-1" /> Reel Category <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    required
                    disabled={loadingCategories}
                    className="border-2"
                  >
                    <option value="">Select a category</option>
                    {reelCategories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </Input>
                  {loadingCategories && <Spinner size="sm" className="mt-2" />}
                  <small className="text-muted d-block mt-1">
                    Choose the category for your reel
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label className="fw-bold text-secondary">
                    <FaBuilding className="me-1" /> Business Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Enter business name"
                    required
                    className="border-2"
                  />
                </FormGroup>

                <FormGroup>
                  <Label className="fw-bold text-secondary">
                    <FaPhone className="me-1" /> Phone Number <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="Enter phone number"
                    required
                    className="border-2"
                  />
                </FormGroup>

                <FormGroup>
                  <Label className="fw-bold text-secondary">
                    <FaVideo className="me-1" /> Upload Video <span className="text-danger">*</span>
                  </Label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <label className="btn btn-outline-primary btn-sm px-4 py-2">
                      <FaUpload className="me-2" /> Choose Video
                      <Input type="file" hidden onChange={handleVideoChange} accept="video/*" required />
                    </label>
                    <span className="text-muted small">Max 50MB</span>
                  </div>
                  <small className="text-warning d-block mt-1">
                    <FaClock className="me-1" /> ⚠️ Video must be <strong>60 seconds or less</strong>
                  </small>
                  <small className="text-muted d-block mt-1">
                    Video will have "EDITEZY" watermark overlay automatically
                  </small>
                </FormGroup>

                <FormGroup className="mt-3">
                  <Label className="fw-bold text-secondary">
                    <FaImage className="me-1" /> Upload Thumbnail <span className="text-danger">*</span>
                  </Label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <label className="btn btn-outline-primary btn-sm px-4 py-2">
                      <FaUpload className="me-2" /> Choose Thumbnail
                      <Input type="file" hidden onChange={handleThumbnailChange} accept="image/*" required={!thumbnailFile} />
                    </label>
                    <span className="text-muted small">Max 5MB</span>
                  </div>
                  <small className="text-danger d-block mt-1">
                    ⚠️ Thumbnail is required for your reel
                  </small>
                </FormGroup>

                <FormGroup className="mt-3">
                  <Label className="fw-bold text-secondary">
                    <FaMusic className="me-1" /> Background Audio (Optional)
                  </Label>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <label className="btn btn-outline-secondary btn-sm px-4 py-2">
                      <FaUpload className="me-2" /> Choose Audio
                      <Input type="file" hidden onChange={handleAudioChange} accept="audio/*" />
                    </label>
                    <span className="text-muted small">Max 10MB</span>
                  </div>
                  <small className="text-warning d-block mt-1">
                    <FaClock className="me-1" /> ⚠️ Audio must be <strong>30 seconds or less</strong>
                  </small>
                  <small className="text-muted d-block">
                    If no audio is uploaded, the original video sound will be used
                  </small>
                </FormGroup>

                {audioPreview && (
                  <div className="mt-2 p-2 border rounded bg-light">
                    <audio controls src={audioPreview} className="w-100" style={{ maxHeight: 50 }}>
                      Your browser does not support the audio element.
                    </audio>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <small className="text-muted">Duration: {audioDuration.toFixed(1)} sec</small>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          setAudioFile(null);
                          setAudioPreview(null);
                          setAudioDuration(0);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}

                {thumbnailPreview && (
                  <div className="mt-3">
                    <img
                      src={thumbnailPreview}
                      alt="Thumbnail preview"
                      className="rounded border w-100"
                      style={{ maxHeight: 150, objectFit: 'cover' }}
                    />
                    <Button
                      color="danger"
                      size="sm"
                      className="mt-1"
                      onClick={() => {
                        setThumbnailFile(null);
                        setThumbnailPreview(null);
                      }}
                    >
                      Remove Thumbnail
                    </Button>
                  </div>
                )}

                <FormGroup className="mt-3">
                  <Label className="d-flex align-items-center gap-2 fw-bold text-secondary">
                    <FaFire className={hotTop ? "text-danger" : "text-muted"} />
                    Mark as Hot/Top Reel
                  </Label>
                  <div className="d-flex align-items-center gap-3">
                    <Input
                      type="switch"
                      checked={hotTop}
                      onChange={(e) => setHotTop(e.target.checked)}
                      style={{ width: '50px', height: '25px' }}
                    />
                    <span className={hotTop ? "text-danger fw-bold" : "text-muted"}>
                      {hotTop ? "🔥 Hot Reel - Highlighted" : "Normal Reel"}
                    </span>
                  </div>
                  <small className="text-muted d-block mt-1">
                    Hot reels appear with a fire badge and get priority visibility
                  </small>
                </FormGroup>

                {videoPreview && (
                  <div className="mt-3">
                    <video
                      src={videoPreview}
                      controls
                      className="w-100 rounded border"
                      style={{ maxHeight: 250, objectFit: 'cover' }}
                    />
                    <Button
                      color="danger"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        setVideoFile(null);
                        setVideoPreview(null);
                      }}
                    >
                      Remove Video
                    </Button>
                  </div>
                )}

                <Button
                  color="primary"
                  className="mt-3 w-100 py-2 fw-bold"
                  disabled={isSubmitting || !videoFile || !thumbnailFile || !selectedCategory || !businessName.trim() || !phoneNumber.trim()}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" className="me-2" /> Uploading...
                    </>
                  ) : (
                    'Create Reel'
                  )}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>

        {/* Table Section */}
        <Col xs={12} lg={7}>
          <Card className="shadow-sm border-0 h-100">
            <CardBody className="p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0">
                  <FaEye className="me-2 text-primary" /> All Reels
                </h5>
                {loading && <Spinner size="sm" color="primary" />}
              </div>

              {loading && reels.length === 0 ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Loading reels...</p>
                </div>
              ) : reels.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaVideo size={40} className="mb-3 text-muted opacity-25" />
                  <p>No reels found. Create your first reel!</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <Table hover className="align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th className="fw-bold">#</th>
                          <th className="fw-bold">Thumbnail</th>
                          <th className="fw-bold">Category</th>
                          <th className="fw-bold">Business</th>
                          <th className="fw-bold">Phone</th>
                          <th className="fw-bold text-center">Audio</th>
                          <th className="fw-bold text-center">Likes</th>
                          <th className="fw-bold text-center">Status</th>
                          <th className="fw-bold text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedReels.map((reel, i) => (
                          <tr key={reel._id}>
                            <td className="text-muted">{(currentPage - 1) * reelsPerPage + i + 1}</td>
                            <td style={{ minWidth: 100 }}>
                              <div
                                style={{ position: 'relative', cursor: 'pointer' }}
                                onClick={() => openVideoModal(reel.videoUrl)}
                                className="position-relative"
                              >
                                <img
                                  src={reel.thumbnailUrl}
                                  alt="Reel thumbnail"
                                  className="rounded border"
                                  style={{ width: 80, height: 80, objectFit: 'cover' }}
                                  onError={(e) => { e.target.src = ''; }}
                                />
                                <div className="position-absolute top-50 start-50 translate-middle">
                                  <FaPlay className="text-white opacity-75" size={20} />
                                </div>
                              </div>
                            </td>
                            <td>
                              <Badge color="info" pill className="px-3 py-2">
                                {getCategoryName(reel.reelCategoryId._id)}
                              </Badge>
                            </td>
                            <td>
                              <strong className="text-dark">{reel.businessName || 'N/A'}</strong>
                            </td>
                            <td>
                              <span className="text-muted">{reel.phoneNumber || 'N/A'}</span>
                            </td>
                            <td className="text-center">
                              {reel.audioUrl ? (
                                <FaMusic className="text-success" size={18} title="Has background audio" />
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center gap-2">
                                <Button
                                  size="sm"
                                  color="link"
                                  onClick={() => handleLikeToggle(reel)}
                                  className="p-0 text-decoration-none"
                                >
                                  {reel.isLiked ? (
                                    <FaHeart className="text-danger" size={18} />
                                  ) : (
                                    <FaRegHeart size={18} className="text-muted" />
                                  )}
                                </Button>
                                <span className="fw-semibold">{reel.likeCount}</span>
                              </div>
                            </td>
                            <td className="text-center">
                              {reel.hotTop && (
                                <Badge color="danger" className="px-3 py-2">
                                  <FaFire size={12} className="me-1" /> Hot
                                </Badge>
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-2 justify-content-center">
                                <Button
                                  size="sm"
                                  color="primary"
                                  onClick={() => openEditModal(reel)}
                                  className="px-3"
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  size="sm"
                                  color="danger"
                                  onClick={() => handleDelete(reel._id)}
                                  className="px-3"
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <small className="text-muted">
                        Showing {(currentPage - 1) * reelsPerPage + 1} to {Math.min(currentPage * reelsPerPage, reels.length)} of {reels.length} entries
                      </small>
                      <Pagination className="mb-0">
                        <PaginationItem disabled={currentPage === 1}>
                          <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                        </PaginationItem>
                        {getPagination().map((p, i) => (
                          <PaginationItem key={i} active={p === currentPage} disabled={p === '...'}>
                            <PaginationLink onClick={() => p !== '...' && handlePageChange(p)}>
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem disabled={currentPage === totalPages}>
                          <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                        </PaginationItem>
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Edit Reel Modal */}
      <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setEditModalOpen(false)} className="border-0 pb-0">
          <div className="d-flex align-items-center">
            <FaEdit className="text-primary me-2" size={20} />
            <span className="fw-bold">Edit Reel</span>
          </div>
        </ModalHeader>
        <Form onSubmit={handleEditSubmit}>
          <ModalBody>
            <FormGroup>
              <Label className="fw-bold text-secondary">Reel Category</Label>
              <Input
                type="select"
                value={editCategoryId}
                onChange={(e) => setEditCategoryId(e.target.value)}
                disabled={loadingCategories}
                className="border-2"
              >
                <option value="">Select a category</option>
                {reelCategories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-bold text-secondary">
                    <FaBuilding className="me-1" /> Business Name
                  </Label>
                  <Input
                    type="text"
                    value={editBusinessName}
                    onChange={(e) => setEditBusinessName(e.target.value)}
                    placeholder="Enter business name"
                    className="border-2"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-bold text-secondary">
                    <FaPhone className="me-1" /> Phone Number
                  </Label>
                  <Input
                    type="tel"
                    value={editPhoneNumber}
                    onChange={(e) => setEditPhoneNumber(e.target.value)}
                    placeholder="Enter phone number"
                    className="border-2"
                  />
                </FormGroup>
              </Col>
            </Row>

            <hr className="my-3" />

            <FormGroup>
              <Label className="fw-bold text-secondary">Update Video (Optional)</Label>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <label className="btn btn-outline-primary btn-sm px-4 py-2">
                  <FaUpload className="me-2" /> Choose New Video
                  <Input type="file" hidden onChange={handleEditVideoChange} accept="video/*" />
                </label>
                <span className="text-muted small">Leave empty to keep current</span>
              </div>
              {editVideoPreview && (
                <div className="mt-2">
                  <video src={editVideoPreview} controls className="w-100 rounded" style={{ maxHeight: 150, objectFit: 'cover' }} />
                  <Button color="danger" size="sm" className="mt-1" onClick={() => {
                    setEditVideoFile(null);
                    setEditVideoPreview(null);
                  }}>Remove New Video</Button>
                </div>
              )}
            </FormGroup>

            <FormGroup className="mt-3">
              <Label className="fw-bold text-secondary">Update Thumbnail (Optional)</Label>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <label className="btn btn-outline-secondary btn-sm px-4 py-2">
                  <FaUpload className="me-2" /> Choose New Thumbnail
                  <Input type="file" hidden onChange={handleEditThumbnailChange} accept="image/*" />
                </label>
                <span className="text-muted small">Leave empty to keep current</span>
              </div>
              {editThumbnailPreview && (
                <div className="mt-2">
                  <img src={editThumbnailPreview} alt="Thumbnail preview" className="rounded border w-100" style={{ maxHeight: 150, objectFit: 'cover' }} />
                  <Button color="danger" size="sm" className="mt-1" onClick={() => {
                    setEditThumbnailFile(null);
                    setEditThumbnailPreview(null);
                  }}>Remove New Thumbnail</Button>
                </div>
              )}
            </FormGroup>

            <FormGroup className="mt-3">
              <Label className="fw-bold text-secondary">Update Background Audio (Optional)</Label>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <label className="btn btn-outline-warning btn-sm px-4 py-2">
                  <FaUpload className="me-2" /> Choose New Audio
                  <Input type="file" hidden onChange={handleEditAudioChange} accept="audio/*" />
                </label>
                <span className="text-muted small">Leave empty to keep current</span>
              </div>
              <small className="text-warning d-block mt-1">
                <FaClock className="me-1" /> Audio must be ≤30 seconds
              </small>
              {editAudioPreview && (
                <div className="mt-2">
                  <audio controls src={editAudioPreview} className="w-100" />
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small className="text-muted">Duration: {editAudioDuration.toFixed(1)} sec</small>
                    <Button color="danger" size="sm" onClick={() => {
                      setEditAudioFile(null);
                      setEditAudioPreview(null);
                      setEditAudioDuration(0);
                    }}>Remove New Audio</Button>
                  </div>
                </div>
              )}
            </FormGroup>

            <hr className="my-3" />

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label className="fw-bold text-secondary">Like Count</Label>
                  <Input
                    type="number"
                    value={editLikeCount}
                    onChange={(e) => setEditLikeCount(parseInt(e.target.value) || 0)}
                    min="0"
                    className="border-2"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup check className="mt-4">
                  <Label check className="fw-bold text-secondary">
                    <Input type="checkbox" checked={editIsLiked} onChange={(e) => setEditIsLiked(e.target.checked)} className="me-2" />
                    Liked Status
                  </Label>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup check className="mt-4">
                  <Label check className="fw-bold text-secondary">
                    <Input type="checkbox" checked={editHotTop} onChange={(e) => setEditHotTop(e.target.checked)} className="me-2" />
                    <FaFire className="text-danger me-1" /> Hot/Top Reel
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter className="border-0 pt-0">
            <Button color="secondary" onClick={() => setEditModalOpen(false)} className="px-4">
              Cancel
            </Button>
            <Button color="primary" type="submit" className="px-4">
              <FaCheckCircle className="me-2" /> Update Reel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Video Preview Modal */}
      <Modal isOpen={videoModal} toggle={() => setVideoModal(false)} centered size="lg">
        <ModalHeader toggle={() => setVideoModal(false)} className="border-0 pb-0">
          <div className="d-flex align-items-center">
            <FaPlay className="text-primary me-2" size={16} />
            <span className="fw-bold">Reel Preview</span>
          </div>
        </ModalHeader>
        <ModalBody className="text-center p-3">
          {selectedVideo && (
            <video
              src={selectedVideo}
              controls
              autoPlay
              className="w-100 rounded shadow-sm"
              style={{ maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </ModalBody>
        <ModalFooter className="border-0 pt-0">
          <Button color="secondary" onClick={() => setVideoModal(false)} className="px-4">
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ManageReels;